// The player (NARRATION.md "Player"): explicit states, one <audio> element playing each part as a WAV, the next part
// prepared while one plays. Progress (part, seconds) is saved locally and synced (SYNC.md).
import { account } from "../api/account";
import { chunks, title, type Article } from "../model/article";
import { library } from "../model/library";
import { narrationVoice } from "../models";
import { decodeVoice, encodeVoice, settings, type NarrationVoice } from "../settings";
import { sync } from "../sync/sync";
import { Hub, isAbort } from "../util";
import { engine, wav, type PartAudio } from "./engine";
import { layoutFor, sentenceAround, wordAtOffset, type NarrationLayout } from "./paragraphMap";
import { wordAt } from "./wordTiming";

export type PlayerState =
  | { kind: "idle" } | { kind: "loading"; part: number } | { kind: "waiting"; part: number; reason: string }
  | { kind: "playing" } | { kind: "paused" } | { kind: "failed"; part: number; message: string };

export interface Spot { paragraph: number; word: [number, number] | null; sentence: [number, number] | null }

class Player {
  articleId: string | null = null;
  part = 0;
  state: PlayerState = { kind: "idle" };
  readonly events = new Hub<{ changed: void }>();
  private el: HTMLAudioElement | null = null;
  private url: string | null = null;
  private audio: PartAudio | null = null;
  private loadToken = 0;
  private lastSave = 0;
  private layoutCache: { id: string; sig: string; layout: NarrationLayout | null } | null = null;

  constructor() { sync.isActivelyPlaying = (id) => id === this.articleId && this.isActive; }

  private element(): HTMLAudioElement {
    if (!this.el) {
      const el = new Audio();
      el.preload = "auto";
      el.addEventListener("ended", () => this.partEnded());
      el.addEventListener("timeupdate", () => this.maybeSave());
      el.addEventListener("pause", () => { if (this.state.kind === "playing" && !el.ended) { this.state = { kind: "paused" }; this.save(); this.emit(); } });
      el.addEventListener("play", () => { if (this.state.kind === "paused") { this.state = { kind: "playing" }; this.emit(); } });
      this.el = el;
      if ("mediaSession" in navigator) {
        navigator.mediaSession.setActionHandler("play", () => this.resume());
        navigator.mediaSession.setActionHandler("pause", () => this.pause());
        navigator.mediaSession.setActionHandler("seekbackward", () => this.skip(-15));
        navigator.mediaSession.setActionHandler("seekforward", () => this.skip(30));
      }
    }
    return this.el;
  }

  get article(): Article | undefined { return this.articleId ? library.get(this.articleId) : undefined; }
  get isLoaded() { return this.articleId !== null && this.state.kind !== "idle"; }
  get isActive() { return ["playing", "loading", "waiting"].includes(this.state.kind); }
  get isPlaying() { return this.state.kind === "playing"; }
  /** Seconds into the current part. */
  get time(): number { return this.el?.currentTime ?? 0; }

  private emit() { this.events.emit("changed", undefined); }

  /** The voice for an article: the one it was started with (so all parts match), unless this account can't use it
   *  (provider not on the server, or a model it may not choose); then this browser's current voice. */
  voiceFor(a: Article): NarrationVoice {
    const recorded = decodeVoice(a.meta.modelName, a.meta.voiceName);
    const mine = narrationVoice();
    if (!recorded || !account.available(recorded.provider)) return mine;
    if (!account.canChooseModels && (recorded.provider !== mine.provider || recorded.model !== mine.model)) return mine;
    return recorded;
  }

  layout(a: Article): NarrationLayout | null {
    const sig = `${a.meta.chunks?.length}|${a.meta.skipped?.length ?? 0}|${(a.meta.text ?? "").length}`;
    if (this.layoutCache?.id === a.id && this.layoutCache.sig === sig) return this.layoutCache.layout;
    const layout = layoutFor(a);
    this.layoutCache = { id: a.id, sig, layout };
    return layout;
  }

  async play(a: Article, part: number, offset = 0): Promise<void> {
    const parts = chunks(a);
    if (!parts.length) return;
    part = Math.max(0, Math.min(parts.length - 1, part));
    if (this.articleId && this.articleId !== a.id) { this.save(); engine.cancel(this.articleId); }
    this.articleId = a.id;
    this.part = part;
    const token = ++this.loadToken;
    const el = this.element();
    el.pause();
    this.state = { kind: "loading", part };
    this.emit();
    const v = this.voiceFor(a);
    if (!a.meta.modelName) library.editMeta(a.id, (m) => { m.modelName = encodeVoice(v); m.voiceName = v.voice; });
    library.markOpened(a.id);
    try {
      const statusTimer = setInterval(() => {
        if (token === this.loadToken && engine.status && this.state.kind === "loading") { this.state = { kind: "waiting", part, reason: engine.status }; this.emit(); }
      }, 500);
      const audio = await engine.part(a.id, part, parts[part], v, true).finally(() => clearInterval(statusTimer));
      if (token !== this.loadToken) return;
      this.audio = audio;
      if (this.url) URL.revokeObjectURL(this.url);
      this.url = URL.createObjectURL(wav(audio.pcm, audio.rate));
      el.src = this.url;
      el.playbackRate = settings.playbackRate;
      await new Promise<void>((r) => { if (el.readyState >= 1) r(); else el.addEventListener("loadedmetadata", () => r(), { once: true }); });
      el.currentTime = Math.min(offset, Math.max(0, el.duration - 0.05));
      this.state = { kind: "playing" };
      this.emit();
      await el.play().catch((e) => { if (token === this.loadToken) { this.state = { kind: "paused" }; this.emit(); } if (e?.name !== "NotAllowedError" && e?.name !== "AbortError") throw e; });
      this.save();
      this.mediaMetadata(a);
      if (part + 1 < parts.length) engine.part(a.id, part + 1, parts[part + 1], v, false).catch(() => {});
    } catch (e) {
      if (isAbort(e) || token !== this.loadToken) return;
      this.state = { kind: "failed", part, message: e instanceof Error ? e.message : String(e) };
      this.emit();
    }
  }

  private mediaMetadata(a: Article) {
    if ("mediaSession" in navigator) navigator.mediaSession.metadata = new MediaMetadata({ title: title(a), artist: a.meta.siteName || a.meta.author || "Depth" });
  }

  private partEnded() {
    const a = this.article;
    if (!a) return;
    const n = chunks(a).length;
    if (this.part + 1 < n) { this.play(a, this.part + 1, 0); return; }
    library.setProgress(a.id, 0, 0, true);
    this.state = { kind: "paused" };
    this.emit();
  }

  pause() {
    if (this.state.kind === "loading" || this.state.kind === "waiting") { this.loadToken++; this.state = { kind: "paused" }; this.emit(); return; }
    this.el?.pause();
  }

  resume() {
    const a = this.article;
    if (!a) return;
    if (!this.audio || this.state.kind === "failed") { this.play(a, this.part, this.time); return; }
    this.el?.play().catch(() => {});
    this.state = { kind: "playing" };
    this.emit();
  }

  toggle() { this.isActive ? this.pause() : this.resume(); }

  skip(seconds: number) {
    const el = this.el, a = this.article;
    if (!el || !a) return;
    const t = el.currentTime + seconds;
    if (t < 0 && this.part > 0) { this.play(a, this.part - 1, 1e9); return; }
    if (t > el.duration && this.part + 1 < chunks(a).length) { this.play(a, this.part + 1, 0); return; }
    el.currentTime = Math.max(0, Math.min(el.duration || 0, t));
    this.save();
  }

  setRate(r: number) { if (this.el) this.el.playbackRate = r; }

  stop() {
    this.save();
    this.loadToken++;
    this.el?.pause();
    if (this.articleId) engine.cancel(this.articleId);
    this.articleId = null;
    this.audio = null;
    this.state = { kind: "idle" };
    this.emit();
  }

  private maybeSave() {
    if (Date.now() - this.lastSave > 2000) this.save();
    this.emit();
  }

  save() {
    if (!this.articleId || !this.audio) return;
    this.lastSave = Date.now();
    library.setProgress(this.articleId, this.part, this.time, false);
  }

  /** The word being spoken (DEPTH.md §6), in reader terms. */
  spot(a: Article): Spot | null {
    if (a.id !== this.articleId || !this.isLoaded) return null;
    const layout = this.layout(a);
    const info = layout?.parts[this.part];
    if (!info) return null;
    const map = this.audio?.timing;
    const w = map && map.words.length === info.words.length && this.state.kind !== "loading" ? wordAt(map, this.time) : null;
    if (w === null || !info.located[w]) {
      const first = info.located.find((x) => x);
      return first ? { paragraph: first.paragraph, word: null, sentence: null } : null;
    }
    const spot = info.located[w]!;
    const [sa, sb] = sentenceAround(info, w);
    const inP = info.located.slice(sa, sb + 1).filter((l) => l && l.paragraph === spot.paragraph) as { start: number; end: number }[];
    return { paragraph: spot.paragraph, word: [spot.start, spot.end], sentence: inP.length ? [inP[0].start, inP[inP.length - 1].end] : null };
  }

  /** Where listening left off (this browser or another device): the word spoken at the stored time. */
  async storedSpot(a: Article): Promise<{ paragraph: number; offset: number; at: number } | null> {
    const parts = chunks(a);
    if (!parts.length || a.progressUpdatedAt <= 0 || (a.progress.chunk === 0 && a.progress.offset === 0) || a.progress.chunk >= parts.length) return null;
    const info = this.layout(a)?.parts[a.progress.chunk];
    if (!info) return null;
    let loc = info.located.find((x) => x) ?? null;
    const key = await engine.key(a.id, a.progress.chunk, parts[a.progress.chunk], this.voiceFor(a));
    const cached = await engine.cached(key);
    if (cached && cached.timing.words.length === info.words.length) {
      const w = wordAt(cached.timing, a.progress.offset);
      if (w !== null && info.located[w]) loc = info.located[w];
    } else {
      // No timing here: estimate the word from the part's time at ~19.5 chars/s.
      const ch = a.progress.offset * 19.5;
      const k = info.words.findLastIndex((x) => x[0] <= ch);
      if (k >= 0 && info.located[k]) loc = info.located[k];
    }
    return loc ? { paragraph: loc.paragraph, offset: loc.start, at: a.progressUpdatedAt } : null;
  }

  /** Narrate from a character of a paragraph (a tap on a word, "Listen from here"). */
  async seek(a: Article, paragraph: number, offset: number, paragraphCount: number): Promise<void> {
    const layout = this.layout(a);
    if (!layout) return;
    let target = wordAtOffset(layout, paragraph, offset);
    for (let p = paragraph + 1; !target && p < paragraphCount; p++) target = wordAtOffset(layout, p, 0);
    if (!target) return;
    let seconds = 0;
    const parts = chunks(a);
    const key = await engine.key(a.id, target.part, parts[target.part], this.voiceFor(a));
    const cached = await engine.cached(key);
    if (cached && cached.timing.words[target.word]) seconds = cached.timing.words[target.word][2];
    else {
      const info = layout.parts[target.part];
      seconds = info.words[target.word][0] / 19.5;
    }
    if (target.part === this.part && a.id === this.articleId && this.audio && this.el && cached) {
      this.el.currentTime = seconds;
      if (!this.isActive) this.resume();
      return;
    }
    await this.play(a, target.part, seconds);
  }
}

export const player = new Player();
